import React, { useCallback, useState } from 'react';

import { BACKEND_ERRORS_TRANSLATION_KEYS } from '../../constants/errors.constants';
import { getExternalErrorTranslationStrings } from '../../helpers/errors.helpers';
import { useGraphMutation } from '../useMutation';

import { validatePictureFormat, validatePictureSize, fileToBase64 } from '@dashboard/library';
import { ProfileImageEntity } from '@generated/globalTypes';
import {
  UploadProfileImageMutation,
  UploadProfileImageMutationVariables,
} from '@generated/UploadProfileImageMutation';
import { UPLOAD_PROFILE_IMAGE_MUTATION } from '@graphql/mutations/UploadProfileImageMutation';
import noop from 'lodash-es/noop';

import {
  UseDeletePicture,
  UseDeletePictureReturn,
  UseUploadPicture,
  UseUploadPictureReturn,
} from '.';

/**
 * Hook to upload a picture.
 * @param UseUploadPicture - destructured parameters.
 * @returns A function that can be called at any time to execute the mutation and an object with fields that represent the current status of the mutation's execution.
 */
export function useUploadPicture<TQuery, TVariables>({
  query,
  updateCache,
  cacheItemId,
  getVariables,
}: UseUploadPicture<TQuery, TVariables>): UseUploadPictureReturn {
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const [uploadPicture] = useGraphMutation<
    UploadProfileImageMutation,
    UploadProfileImageMutationVariables
  >(UPLOAD_PROFILE_IMAGE_MUTATION, {
    onError: () => {
      setUploadError(BACKEND_ERRORS_TRANSLATION_KEYS.UNKNOWN_ERROR);
      setLoading(false);
    },
  });

  const [updatePicture, { error: updateError }] = useGraphMutation<TQuery, TVariables>(query, {
    onCompleted: () => {
      setSaved(true);
      setLoading(false);
    },
    onError: () => setLoading(false),
  });

  const onUpdate = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, entity: ProfileImageEntity) => {
      /* istanbul ignore else */
      if (event.target.files?.[0]) {
        const [file] = event.target.files;
        const validationError = validatePictureFormat(file.type) ?? validatePictureSize(file.size);
        setUploadError(validationError);

        if (validationError) {
          return;
        }

        setLoading(true);
        setSaved(false);

        const base64 = await fileToBase64(file);
        const { data } = await uploadPicture({
          variables: {
            input: {
              base64,
              entity,
            },
          },
        });

        if (!data) {
          return;
        }

        const {
          uploadProfileImage: { url },
        } = data;
        const variables = getVariables(url);

        updatePicture({ variables, update: updateCache?.(cacheItemId, url) });
      }
    },
    [getVariables, updatePicture, updateCache, cacheItemId, uploadPicture]
  );

  return [
    onUpdate,
    {
      loading,
      saved,
      error: uploadError ?? (updateError && getExternalErrorTranslationStrings(updateError)),
    },
  ];
}

/**
 * Hook to delete a picture.
 * @param UseDeletePicture - destructured parameters.
 * @returns A function that can be called at any time to execute the mutation and an object with fields that represent the current status of the mutation's execution.
 */
export function useDeletePicture<TQuery, TVariables>({
  query,
  variables,
  updateCache,
}: UseDeletePicture<TQuery, TVariables>): UseDeletePictureReturn {
  const [saved, setSaved] = useState(false);

  const [updatePicture, { loading, error }] = useGraphMutation<TQuery, TVariables>(query, {
    variables,
    onCompleted: () => {
      setSaved(true);
    },
    onError: noop,
  });

  const deleteProfilePicture = useCallback(() => {
    setSaved(false);
    updatePicture({ update: updateCache });
  }, [updateCache, updatePicture]);

  return [
    deleteProfilePicture,
    {
      loading,
      saved,
      error: error && getExternalErrorTranslationStrings(error),
    },
  ];
}
